import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatLogo from "./ChatLogo";
import clsx from "clsx";
import { MessageProps } from "@/types/componentsTypes";
import MessageOptions from "./message-options/MessageOptions";

const markdownComponents = {
  h1: ({ children }: any) => (
    <h1 className="mb-3 mt-1 text-lg font-semibold text-gray-950">
      {children}
    </h1>
  ),

  h2: ({ children }: any) => (
    <h2 className="mb-2 mt-4 text-base font-semibold text-gray-900">
      {children}
    </h2>
  ),

  h3: ({ children }: any) => (
    <h3 className="mb-1.5 mt-3 text-sm font-semibold text-gray-900">
      {children}
    </h3>
  ),

  h4: ({ children }: any) => (
    <h4 className="mb-1 mt-3 text-sm font-semibold text-gray-800">
      {children}
    </h4>
  ),

  h5: ({ children }: any) => (
    <h5 className="mb-1 mt-2 text-xs font-semibold text-gray-700">
      {children}
    </h5>
  ),

  h6: ({ children }: any) => (
    <h6 className="mb-1 mt-2 text-xs font-medium text-gray-600">{children}</h6>
  ),

  p: ({ children }: any) => (
    <p className="my-2 first:mt-0 last:mb-0 leading-relaxed text-gray-800">
      {children}
    </p>
  ),

  ul: ({ children }: any) => (
    <ul className="my-2 list-disc space-y-1.5 pl-5 marker:text-brand-500">
      {children}
    </ul>
  ),

  ol: ({ children }: any) => (
    <ol className="my-2 list-decimal space-y-1.5 pl-5 marker:font-medium marker:text-brand-500">
      {children}
    </ol>
  ),

  li: ({ children }: any) => (
    <li className="pl-1 leading-relaxed text-gray-800">{children}</li>
  ),

  strong: ({ children }: any) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),

  em: ({ children }: any) => (
    <em className="italic text-gray-600">{children}</em>
  ),

  hr: () => <hr className="my-4 border-gray-200" />,

  blockquote: ({ children }: any) => (
    <blockquote className="my-3 rounded-r-lg border-l-4 border-brand-300 bg-brand-50/40 px-3 py-2 italic text-gray-700">
      {children}
    </blockquote>
  ),

  a: ({ href, children }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
    >
      {children}
    </a>
  ),

  table: ({ children }: any) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),

  th: ({ children }: any) => (
    <th className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-900">
      {children}
    </th>
  ),

  td: ({ children }: any) => (
    <td className="border-b border-gray-100 px-3 py-2 text-gray-700">
      {children}
    </td>
  ),

  pre: ({ children }: any) => (
    <pre className="my-3 overflow-x-auto rounded-lg bg-gray-900 p-3 text-xs leading-relaxed text-gray-100">
      {children}
    </pre>
  ),

  code: ({ inline, children }: any) =>
    inline ? (
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-800">
        {children}
      </code>
    ) : (
      <code className="text-xs">{children}</code>
    ),
};

const _Message = ({
  id,
  content,
  isUser,
  timestamp,
  images,
  options,
  isConversationClosed,
  onOptionSelect,
}: MessageProps) => {
  return (
    <div
      className={clsx("flex", isUser ? "justify-end" : "justify-start")}
      data-message-id={id}
    >
      {!isUser && (
        <div className="mr-2 flex items-start">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600">
            <ChatLogo />
          </div>
        </div>
      )}

      <div className="max-w-2xl">
        <div
          className={clsx(
            "rounded-xl px-4 py-3 text-sm leading-relaxed",
            isUser
              ? "rounded-br-none bg-brand-600 text-white whitespace-pre-wrap"
              : "rounded-bl-none border border-gray-100 bg-white text-gray-900 shadow-sm",
          )}
        >
          {isUser ? (
            content
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>

        {!isUser && images && images.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {images.map((image, index) => (
              <div
                key={index}
                className="aspect-video overflow-hidden rounded-lg"
              >
                <img
                  src={image}
                  alt={`Travel destination ${index + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {!isUser && options && options.length > 0 && (
          <MessageOptions
            options={options}
            onOptionSelect={onOptionSelect}
            isConversationClosed={isConversationClosed}
          />
        )}

        <div className="mt-1 flex justify-between px-1 text-xs text-gray-400">
          {timestamp && (
            <span data-testid="message-time">
              {new Date(timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}

          {isUser && <span className="ml-2">You</span>}
        </div>
      </div>
    </div>
  );
};

export const Message = memo(_Message);
